import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink, RefreshCw, Sparkles, X } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import {
  applyJstAssign,
  createOffice,
  lookupJstOffice,
  officeLabel,
  proposeJstAssign,
  type JstGroup,
  type JstOfficeProposal,
  type JstOfficeSuggestion,
} from "../api";
import { useSelectableOffices, useTaskPolling } from "../hooks";
import { TaskLog } from "./ui";

// Stan per grupa: wybrana instytucja (dla grup Skarbu Państwa bez podpowiedzi),
// wybrany urząd, działki wyłączone przez usera i czy adresować do wydziału/referatu
// (czasem nie chcemy — np. brak pewności, że komórka jest właściwa).
interface GroupState {
  institution: string | null;
  office: string;
  excluded: Set<string>;
  useDepartment: boolean;
}

const firstMatch = (g: JstGroup, institution: string | null) =>
  g.options.find((o) => o.institution === institution)?.registry_matches[0]?.id ?? "";

// Automatyczny przydział właścicieli-instytucji (JST / GDDKiA / KOWR) po kodzie
// własności: backend proponuje grupy (instytucja × jednostka adm.) z kandydatami
// z rejestru urzędów; brakujący urząd można dociągnąć AI (internet) — propozycja
// jest edytowalna i dopiero jawne „Dodaj urząd" tworzy rekord. Sam przydział
// właścicieli następuje dopiero po „Przydziel właścicieli".
export function JstAssignModal({
  connectionId,
  onClose,
}: {
  connectionId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: offices } = useSelectableOffices();

  const proposal = useQuery({
    queryKey: ["pp", "jst-propose", connectionId],
    queryFn: () => proposeJstAssign(connectionId),
    staleTime: 0,
  });

  const [groupState, setGroupState] = useState<Record<string, GroupState>>({});
  const [aiProposals, setAiProposals] = useState<Record<string, JstOfficeProposal | null>>({});
  const [aiNotes, setAiNotes] = useState<Record<string, string>>({});
  const [activeLookup, setActiveLookup] = useState<{ group: string; taskId: string } | null>(null);
  const [showSkipped, setShowSkipped] = useState(false);
  const [applied, setApplied] = useState<{ created: number; skipped: number } | null>(null);

  // Inicjalizacja stanu grup po załadowaniu propozycji (nie nadpisuj wyborów usera).
  useEffect(() => {
    const groups = proposal.data?.groups;
    if (!groups) return;
    setGroupState((prev) => {
      const next = { ...prev };
      for (const g of groups) {
        if (!next[g.key]) {
          next[g.key] = {
            institution: g.institution,
            office: firstMatch(g, g.institution),
            excluded: new Set<string>(),
            useDepartment: true,
          };
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal.data]);

  const patchGroup = (key: string, patch: Partial<GroupState>) =>
    setGroupState((s) => ({ ...s, [key]: { ...s[key], ...patch } }));

  // ── AI lookup brakującego urzędu (jedna grupa naraz) ──
  const lookupTask = useTaskPolling(activeLookup?.taskId ?? null);
  const lookupRunning = !!activeLookup;

  const applySuggestion = (groupKey: string, result: JstOfficeSuggestion) => {
    if (result.existing.length > 0) {
      patchGroup(groupKey, { office: result.existing[0].id });
      setAiNotes((n) => ({ ...n, [groupKey]: `Znaleziono w rejestrze: ${officeLabel(result.existing[0])}` }));
    } else if (result.proposal) {
      setAiProposals((p) => ({ ...p, [groupKey]: result.proposal }));
      setAiNotes((n) => ({
        ...n,
        [groupKey]: result.proposal!.via_web_search
          ? "Propozycja z wyszukiwania w internecie — zweryfikuj przed dodaniem."
          : "Propozycja z wiedzy modelu (bez wyszukiwania) — koniecznie zweryfikuj.",
      }));
    } else {
      setAiNotes((n) => ({ ...n, [groupKey]: "Nie udało się znaleźć urzędu — dodaj go ręcznie." }));
    }
  };

  const lookup = useMutation({
    mutationFn: ({ g, institution }: { g: JstGroup; institution: string }) =>
      lookupJstOffice(connectionId, {
        institution,
        commune: g.commune || undefined,
        county: g.county || undefined,
        voivodeship: g.voivodeship || undefined,
        teryts: g.parcels.slice(0, 5).map((p) => p.teryt),
      }),
    onSuccess: (result, { g }) => {
      if (result.task_id) {
        setActiveLookup({ group: g.key, taskId: result.task_id });
      } else {
        applySuggestion(g.key, result);
      }
    },
    onError: (_e, { g }) =>
      setAiNotes((n) => ({ ...n, [g.key]: "Wyszukiwanie nie powiodło się — dodaj urząd ręcznie." })),
  });

  // Zadanie w tle skończone — odczytaj sugestię z wyniku.
  useEffect(() => {
    if (!activeLookup || !lookupTask.data) return;
    const s = lookupTask.data.status;
    if (s === "success") {
      applySuggestion(
        activeLookup.group,
        (lookupTask.data.result ?? { existing: [], proposal: null }) as JstOfficeSuggestion,
      );
      setActiveLookup(null);
    } else if (["failure", "cancelled"].includes(s)) {
      setAiNotes((n) => ({ ...n, [activeLookup.group]: "Wyszukiwanie nie powiodło się — dodaj urząd ręcznie." }));
      setActiveLookup(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupTask.data?.status]);

  const addProposedOffice = useMutation({
    mutationFn: (groupKey: string) => {
      const p = aiProposals[groupKey]!;
      return createOffice({
        name: p.name,
        kind: p.kind,
        department: p.department,
        street: p.street,
        building_no: p.building_no,
        postal_code: p.postal_code,
        city: p.city,
        phone: p.phone,
        email: p.email,
        notes: [
          p.website ? `WWW: ${p.website}` : "",
          p.source ? `Dodano z wyszukiwania AI (źródło: ${p.source})` : "Dodano z wyszukiwania AI",
        ].filter(Boolean).join("\n"),
      });
    },
    onSuccess: (office, groupKey) => {
      qc.invalidateQueries({ queryKey: ["pp", "offices"] });
      patchGroup(groupKey, { office: office.id });
      setAiProposals((p) => ({ ...p, [groupKey]: null }));
      setAiNotes((n) => ({ ...n, [groupKey]: `Dodano i wybrano: ${office.name}` }));
    },
    onError: (_e, groupKey) =>
      setAiNotes((n) => ({ ...n, [groupKey]: "Nie udało się dodać urzędu — uzupełnij brakujące pola adresu." })),
  });

  // ── zatwierdzenie przydziału ──
  const assignments = useMemo(() => {
    const out: { parcel: string; office: string; use_department: boolean; owner?: string }[] = [];
    for (const g of proposal.data?.groups ?? []) {
      const st = groupState[g.key];
      if (!st?.office) continue;
      for (const p of g.parcels) {
        if (!st.excluded.has(p.id)) {
          out.push({
            parcel: p.id, office: st.office, use_department: st.useDepartment,
            // Właściciel z wypisu (sama nazwa) dostaje urząd — bez drugiego wpisu.
            ...(p.owner ? { owner: p.owner } : {}),
          });
        }
      }
    }
    return out;
  }, [proposal.data, groupState]);

  const apply = useMutation({
    mutationFn: () => applyJstAssign(connectionId, assignments),
    onSuccess: (result) => {
      setApplied({ created: result.created_count, skipped: result.skipped_count });
      qc.invalidateQueries({ queryKey: ["pp", "parcels", connectionId] });
      qc.invalidateQueries({ queryKey: ["pp", "jst-propose", connectionId] });
    },
  });

  const officeOptions = (g: JstGroup, st: GroupState) => {
    const matches = g.options.find((o) => o.institution === st.institution)?.registry_matches ?? [];
    const matchedIds = new Set(matches.map((m) => m.id));
    return [
      ...matches.map((m) => ({ value: m.id, label: `★ ${officeLabel(m)}` })),
      ...(offices ?? [])
        .filter((o) => !matchedIds.has(o.id))
        .map((o) => ({ value: o.id, label: officeLabel(o) })),
    ];
  };

  // Wydział/referat wybranego urzędu — dla checkboxa „adresuj do komórki".
  const selectedDepartment = (g: JstGroup, st: GroupState): string =>
    g.options.flatMap((o) => o.registry_matches).find((m) => m.id === st.office)?.department ||
    (offices ?? []).find((o) => o.id === st.office)?.department ||
    "";

  const setProposalField = (groupKey: string, k: keyof JstOfficeProposal, v: string) =>
    setAiProposals((prev) => {
      const p = prev[groupKey];
      return p ? { ...prev, [groupKey]: { ...p, [k]: v } } : prev;
    });

  const groups = proposal.data?.groups ?? [];
  const skipped = proposal.data?.skipped ?? [];

  return (
    <Modal open onClose={onClose} title="Automatyczny przydział urzędów (JST / instytucje)" size="lg">
      <div className="space-y-4">
        <p className="text-xs text-[var(--color-text-secondary)]">
          Propozycja na podstawie kodu własności (grupy rejestrowej) i położenia
          administracyjnego działek. Nic nie zostanie zapisane bez zatwierdzenia — dane
          urzędów z internetu pobieramy dla: GDDKiA, KOWR, gmin, starostw, dróg
          powiatowych i wojewódzkich, Lasów Państwowych (nadleśnictwo z mapy BDL)
          oraz Wód Polskich (jednostka z Hydroportalu).
        </p>

        {proposal.isLoading && (
          <div className="text-sm text-[var(--color-text-muted)]">Przygotowuję propozycję…</div>
        )}
        {proposal.isError && (
          <div className="text-xs text-red-600">Nie udało się przygotować propozycji.</div>
        )}
        {!proposal.isLoading && groups.length === 0 && (
          <div className="text-sm text-[var(--color-text-muted)]">
            Brak działek do automatycznego przydziału (sprawdź kody własności i dane
            administracyjne działek).
          </div>
        )}

        {groups.map((g) => {
          const st = groupState[g.key];
          if (!st) return null;
          const included = g.parcels.filter((p) => !st.excluded.has(p.id));
          const aiP = aiProposals[g.key];
          const note = aiNotes[g.key];
          const thisLookupRunning =
            (lookup.isPending && lookup.variables?.g.key === g.key) ||
            activeLookup?.group === g.key;
          const label =
            g.options.find((o) => o.institution === st.institution)?.label ??
            "Skarb Państwa — wybierz instytucję";
          return (
            <div key={g.key} className="border border-[var(--color-border)] p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold">
                  {/* "powiat Rybnik" ma już prefiks w danych — nie dublować "powiat powiat …". */}
                  {label} · {g.unit_name.toLowerCase().startsWith(g.scope_label)
                    ? g.unit_name
                    : `${g.scope_label} ${g.unit_name}`}
                </span>
                <span className="section-label">{included.length} z {g.parcels.length} działek</span>
              </div>
              {g.owner_name && (
                <div className="text-[11px] text-[var(--color-text-secondary)]">
                  {g.reason} Urząd zostanie podpięty do tego właściciela (bez nowego wpisu).
                </div>
              )}

              {g.requires_choice && (
                <div className="flex gap-4">
                  {g.options.map((o) => (
                    <label key={o.institution} className="flex items-center gap-2 text-xs cursor-pointer">
                      <input
                        type="radio"
                        checked={st.institution === o.institution}
                        onChange={() =>
                          patchGroup(g.key, {
                            institution: o.institution,
                            office: firstMatch(g, o.institution),
                          })
                        }
                      />
                      {o.label}
                    </label>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-1">
                {g.parcels.map((p) => {
                  const off = st.excluded.has(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      title={`kod wł. ${p.ownership_code}${p.land_use ? ` · użytek ${p.land_use}` : ""} — kliknij, by ${off ? "przywrócić" : "pominąć"}`}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-mono border ${
                        off
                          ? "border-[var(--color-border)] text-[var(--color-text-muted)] line-through"
                          : "border-[var(--color-border)] bg-[var(--color-bg-elevated)]"
                      }`}
                      onClick={() => {
                        const excluded = new Set(st.excluded);
                        if (off) excluded.delete(p.id);
                        else excluded.add(p.id);
                        patchGroup(g.key, { excluded });
                      }}
                    >
                      {p.teryt}
                      {!off && <X size={11} />}
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center gap-1">
                <div className="flex-1">
                  <SearchableSelect
                    options={officeOptions(g, st)}
                    value={st.office}
                    placeholder={
                      st.institution
                        ? "Wybierz urząd z rejestru…"
                        : "Najpierw wybierz instytucję…"
                    }
                    onChange={(e) => patchGroup(g.key, { office: e.target.value })}
                  />
                </div>
                <button
                  type="button"
                  className="p-1.5 border border-[var(--color-border-strong)] hover:bg-[var(--color-bg-elevated)]"
                  title="Odśwież listę urzędów"
                  onClick={() => qc.invalidateQueries({ queryKey: ["pp", "offices"] })}
                >
                  <RefreshCw size={13} />
                </button>
                <button
                  type="button"
                  className="p-1.5 border border-[var(--color-border-strong)] hover:bg-[var(--color-bg-elevated)] disabled:opacity-50"
                  title="Pobierz dane urzędu z internetu (AI: rejestr → BIP/strona urzędu)"
                  disabled={!st.institution || lookupRunning || lookup.isPending}
                  onClick={() => {
                    setAiNotes((n) => ({ ...n, [g.key]: "" }));
                    setAiProposals((p) => ({ ...p, [g.key]: null }));
                    lookup.mutate({ g, institution: st.institution! });
                  }}
                >
                  <Sparkles size={13} className={thisLookupRunning ? "animate-pulse" : ""} />
                </button>
              </div>

              {!!selectedDepartment(g, st) && (
                <label className="flex items-center gap-2 text-[11px] text-[var(--color-text-secondary)] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={st.useDepartment}
                    onChange={(e) => patchGroup(g.key, { useDepartment: e.target.checked })}
                  />
                  Adresuj do komórki: {selectedDepartment(g, st)} — odznacz, jeśli wolisz
                  ogólny adres urzędu (np. brak pewności, że to właściwy wydział)
                </label>
              )}

              {thisLookupRunning && (
                <div className="text-[11px] text-[var(--color-text-muted)] space-y-0.5">
                  <div className="flex items-center gap-1">
                    <Sparkles size={11} className="animate-pulse" /> Szukam danych urzędu…
                  </div>
                  <TaskLog log={lookupTask.data?.log ?? ""} />
                </div>
              )}
              {note && !thisLookupRunning && (
                <div className="text-[11px] text-[var(--color-text-secondary)]">{note}</div>
              )}

              {aiP && (
                <div className="border border-dashed border-[var(--color-border-strong)] p-3 space-y-2">
                  <span className="section-label flex items-center gap-1">
                    <Sparkles size={12} /> Proponowane dane urzędu — zweryfikuj i dodaj
                  </span>
                  {(aiP.contact_url || aiP.website || aiP.source) && (
                    <a
                      href={aiP.contact_url || aiP.website || aiP.source}
                      target="_blank"
                      rel="noreferrer"
                      title={aiP.contact_url || aiP.website || aiP.source}
                      className="inline-flex items-center gap-1 text-[11px] text-[var(--color-primary)] hover:underline"
                    >
                      <ExternalLink size={11} /> Sprawdź adres i kontakt na stronie urzędu
                    </a>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    {([
                      ["name", "Nazwa urzędu *", ""],
                      ["department", "Wydział / komórka", "np. Wydział Dróg"],
                      ["street", "Ulica *", ""],
                      ["building_no", "Nr budynku *", ""],
                      ["postal_code", "Kod pocztowy *", "00-000"],
                      ["city", "Miejscowość *", ""],
                      ["phone", "Telefon", ""],
                      ["email", "E-mail", ""],
                    ] as [keyof JstOfficeProposal, string, string][]).map(([field, fieldLabel, ph]) => (
                      <label key={field} className="block">
                        <span className="section-label">{fieldLabel}</span>
                        <input
                          className="input-base mt-0.5 text-xs"
                          value={String(aiP[field] ?? "")}
                          placeholder={ph}
                          onChange={(e) => setProposalField(g.key, field, e.target.value)}
                        />
                      </label>
                    ))}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setAiProposals((p) => ({ ...p, [g.key]: null }))}
                    >
                      Odrzuć
                    </Button>
                    <Button
                      size="sm"
                      loading={addProposedOffice.isPending}
                      disabled={!aiP.name || !aiP.street || !aiP.building_no || !aiP.postal_code || !aiP.city}
                      onClick={() => addProposedOffice.mutate(g.key)}
                    >
                      Dodaj urząd i wybierz
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {skipped.length > 0 && (
          <div>
            <button
              type="button"
              className="flex items-center gap-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
              onClick={() => setShowSkipped((s) => !s)}
            >
              {showSkipped ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              Pominięte działki ({skipped.length})
            </button>
            {showSkipped && (
              <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
                Ten sam powód widać na karcie działki (zakładka „Właściciele”) — tam jest też
                skrót „Przypisz urząd” dla właściciela-instytucji wpisanego samą nazwą.
              </p>
            )}
            {showSkipped && (
              <ul className="mt-1 space-y-0.5 text-[11px] text-[var(--color-text-muted)] max-h-40 overflow-y-auto">
                {skipped.map((s) => (
                  <li key={s.id}>
                    <span className="font-mono">{s.teryt}</span> — {s.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {applied && (
          <div className="text-xs text-[var(--color-text-secondary)] border border-[var(--color-border)] p-2">
            Przydzielono urzędy {applied.created} działkom
            {applied.skipped > 0 ? `, pominięto ${applied.skipped} (już przypisane)` : ""}.
          </div>
        )}
        {apply.isError && (
          <div className="text-xs text-red-600">Nie udało się zapisać przydziału.</div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {applied ? "Zamknij" : "Anuluj"}
          </Button>
          <Button
            onClick={() => apply.mutate()}
            loading={apply.isPending}
            disabled={assignments.length === 0 || lookupRunning}
          >
            Przydziel właścicieli ({assignments.length})
          </Button>
        </div>
      </div>
    </Modal>
  );
}
